import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Pure function — derives which navbar tab should be highlighted from the
 * current pathname.  This is the SINGLE source of truth for active nav state.
 *
 * Route → tab mapping (matches the brief exactly):
 *   /                        → home
 *   /shop                    → shop
 *   /element/:type           → element
 *   /cart, /checkout         → cart
 *   /orders, /orders/:id     → orders
 *   / (scroll sections)      → hero | collection | about  (overridden by scroll hook)
 */
export function getNavTab(pathname) {
  if (pathname === '/') return 'hero';
  if (pathname.startsWith('/shop')) return 'collection';
  if (pathname.startsWith('/element')) return 'element';
  if (pathname === '/cart' || pathname.startsWith('/checkout')) return 'cart';
  if (pathname.startsWith('/orders')) return 'orders';
  if (pathname.startsWith('/about')) return 'about';
  // All other routes (profile, quiz, admin, etc.) → no tab highlighted
  return null;
}

export default function useNavbarScroll(navbarHeight = 96) {
  const location = useLocation();
  const navigate = useNavigate();

  // activeSection is location-derived by default.
  // On the home page it may be further refined by the scroll-position tracker below.
  const [activeSection, setActiveSection] = useState(() => getNavTab(location.pathname));
  const [scrollTarget, setScrollTarget] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Keep activeSection in sync whenever the route changes.
  // On non-home pages this is the only source of truth.
  // On the home page the scroll listener below will refine it further.
  useEffect(() => {
    const tab = getNavTab(location.pathname);
    if (tab !== null) {
      setActiveSection(tab);
    }
    // Clear any pending scroll target on route change
    setScrollTarget(null);
  }, [location.pathname]);

  // Scroll to section function
  const handleScroll = useCallback(
    (id) => {
      if (location.pathname !== '/') {
        setScrollTarget(id);
        navigate('/', { state: { scrollTo: id } });
        return;
      }

      setActiveSection(id);
      const element = document.getElementById(id);
      if (element) {
        // Navbar is 80px on mobile (pt-20), 96px on desktop (pt-24)
        const isMobile = window.innerWidth < 768;
        const offset = isMobile ? 80 : 96;
        const targetY = element.offsetTop - offset;
        window.scrollTo({
          top: targetY,
          behavior: 'smooth',
        });
      }
    },
    [location.pathname, navigate]
  );

  // Highlight sections on scroll — only active on the home page
  useEffect(() => {
    if (location.pathname !== '/') return;

    const sections = ['hero', 'collection', 'about'];

    const handleScrollEvent = () => {
      if (scrollTarget) return; // skip when navigating from another page
      const scrollPos = window.scrollY + navbarHeight;
      for (let i = sections.length - 1; i >= 0; i--) {
        const el = document.getElementById(sections[i]);
        if (el && scrollPos >= el.offsetTop) {
          setActiveSection(sections[i]);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScrollEvent);
    return () => window.removeEventListener('scroll', handleScrollEvent);
  }, [location.pathname, scrollTarget, navbarHeight]);

  // When navigation arrives with a scroll target (from handleScroll on other pages),
  // keep highlighting paused until the smooth scroll completes. Home.jsx performs
  // a smooth scroll of ~600ms; we clear the scrollTarget after that duration so
  // the scroll-based highlight logic resumes.
  useEffect(() => {
    const target = location.state && location.state.scrollTo;
    if (location.pathname === '/' && target && !isInitialLoad) {
      setScrollTarget(target);
      const timer = setTimeout(() => setScrollTarget(null), 750);
      return () => clearTimeout(timer);
    }
    setIsInitialLoad(false);
  }, [location.pathname, location.state, isInitialLoad]);

  return { activeSection, handleScroll, setActiveSection };
}
